import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Iconify from 'src/components/Iconify';
import MktCompactHeader from 'src/sections/@dashboard/marketing/components/MktCompactHeader';
import MktLoading from 'src/sections/@dashboard/marketing/components/MktLoading';
import MktScopeAlert from 'src/sections/@dashboard/marketing/components/MktScopeAlert';
import PeriodFilter from 'src/sections/@dashboard/marketing/components/PeriodFilter';
import useAuth from 'src/hooks/useAuth';
import { MKT_ANALYTICS_DESIGN as d } from './MktAnalytics.design';
import OverviewTab from './OverviewTab';
import InstagramTab from './InstagramTab';
import FacebookTab from './FacebookTab';
import GoogleAdsTab from './GoogleAdsTab';
import {
    fetchOverview,
    fetchInstagramInsights,
    fetchFacebookInsights,
    fetchGoogleAdsInsights,
    setActiveTab
} from 'src/redux/slices/mktAnalytics';

function getTabs(t) {
    return [
        { id: 'overview', label: t('analytics.tabs.overview'), icon: 'eva:grid-fill' },
        { id: 'instagram', label: t('analytics.tabs.instagram'), icon: 'mdi:instagram' },
        { id: 'facebook', label: t('analytics.tabs.facebook'), icon: 'mdi:facebook' },
        { id: 'google_ads', label: t('analytics.tabs.googleAds'), icon: 'mdi:google-ads' },
    ];
}

function getDateRange(period) {
    const end = new Date();
    const start = new Date();

    switch (period) {
        case 'last-7-days': start.setDate(end.getDate() - 7); break;
        case 'last-14-days': start.setDate(end.getDate() - 14); break;
        case 'last-30-days': start.setDate(end.getDate() - 30); break;
        case 'last-90-days': start.setDate(end.getDate() - 90); break;
        default: start.setDate(end.getDate() - 7);
    }

    return {
        start: start.toISOString().split('T')[0],
        end: end.toISOString().split('T')[0]
    };
}

export default function MktAnalytics() {
    const { t } = useTranslation('marketing');
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { insights, activeTab, isLoading, error } = useSelector(state => state.mktAnalytics);

    const tabs = useMemo(() => getTabs(t), [t]);

    const [period, setPeriod] = useState('last-7-days');
    const [customStart, setCustomStart] = useState('');
    const [customEnd, setCustomEnd] = useState('');

    const dateRange = useMemo(() => {
        if (period === 'custom' && customStart && customEnd) {
            return { start: customStart, end: customEnd };
        }
        return getDateRange(period);
    }, [period, customStart, customEnd]);

    const querys = useMemo(() => {
        return `?start_date=${dateRange.start}&end_date=${dateRange.end}`;
    }, [dateRange]);

    const loadData = useCallback(() => {
        const clienteId = user?.clienteId;
        if (!clienteId) return;

        switch (activeTab) {
            case 'overview':
                dispatch(fetchOverview(clienteId, querys));
                break;
            case 'instagram':
                dispatch(fetchInstagramInsights(clienteId, querys));
                break;
            case 'facebook':
                dispatch(fetchFacebookInsights(clienteId, querys));
                break;
            case 'google_ads':
                dispatch(fetchGoogleAdsInsights(clienteId, querys));
                break;
        }
    }, [dispatch, user?.clienteId, activeTab, querys]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleTabChange = (tabId) => {
        dispatch(setActiveTab(tabId));
    };

    const handleReconnect = () => {
        navigate('/dashboard/configuracoes/contas-sociais');
    };

    // O overview responde 200 com dados parciais (as outras plataformas ainda
    // renderizam), entao o aviso acompanha o conteudo. Nas abas de uma
    // plataforma so nao ha o que mostrar alem do erro.
    const blockedByError = Boolean(error) && activeTab !== 'overview';

    return (
        <div className={d.page.containerCompact}>
            <MktCompactHeader
                icon="eva:activity-fill"
                title={t('analytics.title')}
                middle={(
                    <PeriodFilter
                        period={period}
                        onPeriodChange={setPeriod}
                        startDate={customStart}
                        endDate={customEnd}
                        onStartDateChange={setCustomStart}
                        onEndDateChange={setCustomEnd}
                    />
                )}
            />

            <div className={d.page.content}>
            {/* Tabs */}
            <div className={d.tabs.container}>
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        className={activeTab === tab.id ? d.tabs.tabActive : d.tabs.tab}
                        onClick={() => handleTabChange(tab.id)}
                    >
                        <span className="flex items-center gap-1.5">
                            <Iconify icon={tab.icon} width={16} />
                            {tab.label}
                        </span>
                    </button>
                ))}
            </div>

            {/* Loading */}
            {isLoading && <MktLoading />}

            {/* Permissao faltando / conexao expirada / falha da Meta */}
            {!isLoading && error && (
                <MktScopeAlert error={error} onReconnect={handleReconnect} onRetry={loadData} />
            )}

            {/* Tab Content */}
            {!isLoading && !blockedByError && activeTab === 'overview' && <OverviewTab data={insights.overview} />}
            {!isLoading && !blockedByError && activeTab === 'instagram' && <InstagramTab data={insights.instagram} />}
            {!isLoading && !blockedByError && activeTab === 'facebook' && <FacebookTab data={insights.facebook} />}
            {!isLoading && !blockedByError && activeTab === 'google_ads' && <GoogleAdsTab data={insights.googleAds} />}
            </div>
        </div>
    );
}
